import { useMutation } from "@tanstack/react-query";
import { useCallback, useContext } from "react";
import fraction, { AnswerIDVerificationArgs, InitiateIDVerificationArgs } from "src/api/fraction";
import { ApplicationFlowStateContext } from "src/apps/AppFlow/screens/root";
import { ApplicationFlowState } from "src/apps/AppFlow/screens/root/Layout";
import { ReducerReturn } from "src/hooks/useReducerPageState";

import { FractionError, enums, types } from "@fraction/shared";

export type IdQuestionArgs = Partial<ReducerReturn<ApplicationFlowState>>;

export const useIdentityVerificationQuestions = () => {
  const context = useContext(ApplicationFlowStateContext);
  return useIdentityVerificationQuestionsWithContext({ ...context });
};

export const useIdentityVerificationQuestionsWithContext = ({ state, dispatch }: IdQuestionArgs) => {
  const handleSuccess = useCallback(
    async (results: any) => {
      if (types.isSuccessfulAttempt(results)) {
        dispatch?.({
          type: "change",
          // @ts-ignore
          prop: ["idVerificationQuestions", "decision"],
          value: results.attempt?.decision,
        });
      } else if (types.isSuccessfulChallenge(results) && results.nextChallengeData?.kiq) {
        dispatch?.({
          type: "change",
          prop: ["idVerificationQuestions"],
          value: {
            creditBureau: results.attempt?.provider,
            attemptId: results.attempt?.id,
            decision: results.attempt?.decision,
            // in the future, we may have multiple requests, but for now, we get all the questions
            // on the first request. doing it this way makes it easier to restart the verification flow
            // if the user decides to.
            questions: results.nextChallengeData?.kiq || [],
          },
        });
      } else if (types.isFailedChallenge(results)) {
        dispatch?.({
          type: "change",
          // @ts-ignore
          prop: ["idVerificationQuestions", "decision"],
          value: results.attempt?.decision,
        });
      } else {
        throw new FractionError(`Results are in an unexpected state: ${JSON.stringify(results)}`);
      }
    },
    [dispatch]
  );

  const handleError = useCallback(async () => {
    dispatch?.({
      type: "change",
      // @ts-ignore
      prop: ["idVerificationQuestions", "decision"],
      value: enums.IdVerificationChallengeResult.IMPLICIT_FAIL,
    });
  }, [dispatch]);

  // Do query for questions based on applicant information
  const initiateVerification = useMutation({
    mutationFn: (args: InitiateIDVerificationArgs) => fraction.initiateIDVerification(args),
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const answerQuestions = useMutation({
    mutationFn: (args: AnswerIDVerificationArgs) => fraction.continueIDVerification(args),
    onSuccess: handleSuccess,
    onError: handleError,
  });

  // we will be appending questions based on answers and return
  return {
    creditBureau: state?.idVerificationQuestions?.creditBureau,
    questions: state?.idVerificationQuestions?.questions || [],
    decision: state?.idVerificationQuestions?.decision,
    initiateVerification: initiateVerification.mutateAsync,
    answerQuestions: answerQuestions.mutateAsync,
    loading: initiateVerification.isPending || answerQuestions.isPending,
    attemptId: state?.idVerificationQuestions?.attemptId,
  };
};
